import { type ComponentPropsWithRef, type ComponentType, useMemo } from "react";
import { type RouteObject, useNavigate } from "react-router-dom-v5-compat";
import { mapQueryResponse } from "@b2bportal/core-utilities";
import { Product } from "@b2bportal/purchase-api";
import type { PackageItinerarySummary } from "@b2bportal/rbc-bundled-packages-api";
import type { TripProduct } from "@overrides/types";
import { PackageReservationDetailPage } from "../components/PackageReservationDetailPage";
import { packagesPostBookingApi } from "../store";
import { PackageReservationCard } from "@/components/packages/PackageReservationCard";

type PackageTripProduct = TripProduct<PackageItinerarySummary>;

export const usePackageTripProduct = ({
  tripsPath,
  reservationPath,
}: {
  tripsPath: string;
  reservationPath: (reservationId: string) => string;
}): {
  packages: { [Product.Package]: PackageTripProduct };
  pastPackages: { [Product.Package]: PackageTripProduct };
  packageReservationRouteProps: RouteObject;
} => {
  const reservationsQuery =
    packagesPostBookingApi.endpoints.fetchAllPackageReservations.useQuery();

  const navigate = useNavigate();

  const packageProduct: Omit<PackageTripProduct, "reservations"> = useMemo(
    () => ({
      fetch: () => {
        if (
          reservationsQuery.status !== "uninitialized" &&
          reservationsQuery.status !== "pending"
        ) {
          return reservationsQuery.refetch();
        }
        return Promise.resolve({ data: undefined });
      },
      component: ({
        reservation,
        ...props
      }: ComponentPropsWithRef<
        ComponentType<{ reservation: PackageItinerarySummary }>
      >) => (
        <PackageReservationCard
          reservation={reservation}
          selectItinerary={() =>
            navigate(reservationPath(reservation.hopperReservationId))
          }
          {...props}
        />
      ),
    }),
    [reservationPath, reservationsQuery, navigate]
  );

  return useMemo(
    () => ({
      packages: {
        [Product.Package]: {
          ...packageProduct,
          reservations: mapQueryResponse(
            reservationsQuery,
            (data) => data?.current || []
          ),
        },
      },
      pastPackages: {
        [Product.Package]: {
          ...packageProduct,
          reservations: mapQueryResponse(
            reservationsQuery,
            (data) => data?.past || []
          ),
        },
      },
      packageReservationRouteProps: {
        path: reservationPath(":reservationId"),
        element: <PackageReservationDetailPage tripsRoute={tripsPath} />,
      },
    }),
    [packageProduct, reservationsQuery, reservationPath, tripsPath]
  );
};
